import { useTranslation } from "react-i18next";

import { useFramework, frameworkLabel } from "../framework";
import { POUS } from "../framework/data";
import type { PouId, SubdomainId } from "../framework/types";

/**
 * Resolves framework labels in the current language, honouring admin renames and
 * the i18n catalogue (via frameworkLabel). Pou names come straight from the
 * canonical te reo (POUS) — they are not admin-editable.
 */
export function useLabels() {
  const { t, i18n } = useTranslation();
  const fw = useFramework();
  const lang = i18n.language;

  return {
    subdomain: (id: string): string => {
      const s = fw.findSubdomain(id);
      return s ? frameworkLabel(s, "subdomain", lang, t) : id;
    },
    // `id` is widened to string: these resolve server-provided framework ids
    // (e.g. ActivityView's CurrentStateGroup.id, which spans Pou and domain),
    // mirroring fw.findDomain/findPou which already accept string and fall back
    // to the raw id for anything unrecognised.
    domain: (id: string): string => {
      const d = fw.findDomain(id);
      return d ? frameworkLabel(d, "domain", lang, t) : id;
    },
    pou: (id: string): string => POUS.find((p) => p.id === id)?.nameMi ?? id,
    pouDescription: (id: PouId): string =>
      POUS.find((p) => p.id === id)?.description ?? "",
    toc: (id: SubdomainId): string => fw.findSubdomain(id)?.toCOutcome ?? "",
  };
}
